import {Injectable, signal} from '@angular/core';

/* ══════════════════════════════════════════════════════════════
   Console event notifications — the single place every success /
   error / warning message in the app goes through.

   Adding a new kind is a three-line change: add it to NoticeKind, give it a
   default duration in DURATION, and add its glyph to the @switch in
   notify-host.component.html. Nothing else needs to know.

   Usage from anywhere:
     constructor(private notify: NotifyService) {}
     this.notify.success('Message sent');
     this.notify.error('Could not send', {detail: 'Check your connection.'});
     this.notify.success('Conversation archived', {
       action: {label: 'Undo', run: () => this.restore()}
     });
   ══════════════════════════════════════════════════════════════ */

export type NoticeKind = 'success' | 'error' | 'warning' | 'info';

export interface NoticeAction {
  label: string;
  run: () => void;
  /** leave the notice up after the action runs (default: dismiss it) */
  keepOpen?: boolean;
}

export interface NoticeInput {
  /** the headline — the only thing that is ever required */
  text: string;
  /** a second line for the part nobody needs at a glance (a reason, a hint) */
  detail?: string;
  /** e.g. {label: 'Undo', run: () => …} */
  action?: NoticeAction;
  /** ms on screen. 0 keeps it up until dismissed. Defaults per kind. */
  duration?: number;
}

export interface Notice {
  id: number;
  kind: NoticeKind;
  text: string;
  detail?: string;
  action?: NoticeAction;
  duration: number;
  /** a repeat collapses into the existing row and bumps this instead of
   *  stacking five identical messages */
  count: number;
  /** drives the exit animation before the row is actually removed */
  leaving: boolean;
}

/** How long each kind stays up. An error is the one you most need to read, so
 *  it gets the longest; a plain success is a glance and gone. */
const DURATION: Record<NoticeKind, number> = {
  success: 4000,
  info: 4500,
  warning: 6500,
  error: 9000
};

/** Anything with an action needs long enough to actually reach for it. */
const MIN_WITH_ACTION = 7000;

/** Beyond this the stack stops being information and becomes a wall. */
const MAX_VISIBLE = 4;

const EXIT_MS = 200;

@Injectable({providedIn: 'root'})
export class NotifyService {
  private readonly list = signal<Notice[]>([]);
  /** oldest first — the host renders newest nearest the screen edge */
  readonly notices = this.list.asReadonly();

  private seq = 0;
  private readonly timers = new Map<number, { handle: ReturnType<typeof setTimeout>, endsAt: number }>();
  private paused = false;

  success(text: string, opts: Omit<NoticeInput, 'text'> = {}): number {
    return this.show('success', {text, ...opts});
  }

  error(text: string, opts: Omit<NoticeInput, 'text'> = {}): number {
    return this.show('error', {text, ...opts});
  }

  warning(text: string, opts: Omit<NoticeInput, 'text'> = {}): number {
    return this.show('warning', {text, ...opts});
  }

  info(text: string, opts: Omit<NoticeInput, 'text'> = {}): number {
    return this.show('info', {text, ...opts});
  }

  show(kind: NoticeKind, input: NoticeInput): number {
    const duration = input.duration ?? (input.action
      ? Math.max(DURATION[kind], MIN_WITH_ACTION)
      : DURATION[kind]);

    // The same message firing again is almost always one loop reporting per
    // item, not four separate things worth four rows.
    const twin = this.list().find(n => !n.leaving && n.kind === kind
      && n.text === input.text && n.detail === input.detail);
    if (twin) {
      // `duration` rides along too. `arm()` below re-arms the dismissal timer
      // with the duration computed for THIS call, which is not always the twin's
      // — a repeat that carries an action is held for `MIN_WITH_ACTION`. Leaving
      // the field behind meant the countdown hairline (`--life` is bound from it)
      // described a different span from the timer that actually dismisses the row.
      this.list.update(all => all.map(n =>
        n.id === twin.id
          ? {...n, count: n.count + 1, action: input.action ?? n.action, duration}
          : n));
      this.arm(twin.id, duration);
      return twin.id;
    }

    const notice: Notice = {
      id: ++this.seq,
      kind,
      text: input.text,
      detail: input.detail,
      action: input.action,
      duration,
      count: 1,
      leaving: false
    };
    this.list.update(all => {
      const next = [...all, notice];
      // drop the oldest still-settled rows, not whatever happens to be first
      const live = next.filter(n => !n.leaving);
      const over = live.length - MAX_VISIBLE;
      if (over > 0) {
        live.slice(0, over).forEach(n => this.stopTimer(n.id));
        const doomed = new Set(live.slice(0, over).map(n => n.id));
        return next.filter(n => !doomed.has(n.id));
      }
      return next;
    });
    this.arm(notice.id, duration);
    return notice.id;
  }

  /** run a notice's action, then close it unless it asked to stay */
  runAction(notice: Notice): void {
    notice.action?.run();
    if (!notice.action?.keepOpen) {
      this.dismiss(notice.id);
    }
  }

  dismiss(id: number): void {
    this.stopTimer(id);
    if (!this.list().some(n => n.id === id && !n.leaving)) {
      return;
    }
    this.list.update(all => all.map(n => n.id === id ? {...n, leaving: true} : n));
    setTimeout(() => {
      this.list.update(all => all.filter(n => n.id !== id));
      // A pause belongs to a pointer resting on the stack, and the stack stops
      // existing the moment the last row goes (the host's `@if` removes it), so
      // its `pointerleave` can never arrive. Left latched, `paused` outlives the
      // element that set it and `arm()` then hands every LATER notice a dead
      // timer — the whole queue stops expiring. Nothing is hovering an element
      // that is gone, so clearing it here is simply true.
      if (!this.list().length) {
        this.paused = false;
      }
    }, EXIT_MS);
  }

  clear(): void {
    this.list().forEach(n => this.dismiss(n.id));
  }

  /* ── pause while the pointer is on the stack ────────────────
     A message you are reading — or reaching for the Undo of — must not time
     out under the cursor. */
  hold(): void {
    if (this.paused) {
      return;
    }
    this.paused = true;
    for (const [id, timer] of this.timers) {
      clearTimeout(timer.handle);
      this.timers.set(id, {handle: timer.handle, endsAt: timer.endsAt - Date.now()});
    }
  }

  resume(): void {
    if (!this.paused) {
      return;
    }
    this.paused = false;
    for (const [id, timer] of [...this.timers]) {
      this.arm(id, Math.max(400, timer.endsAt));
    }
  }

  private arm(id: number, duration: number): void {
    this.stopTimer(id);
    if (!duration) {
      return;      // sticky: dismissed by hand only
    }
    if (this.paused) {
      this.timers.set(id, {handle: 0 as never, endsAt: duration});
      return;
    }
    this.timers.set(id, {
      handle: setTimeout(() => this.dismiss(id), duration),
      endsAt: Date.now() + duration
    });
  }

  private stopTimer(id: number): void {
    const timer = this.timers.get(id);
    if (timer) {
      clearTimeout(timer.handle);
      this.timers.delete(id);
    }
  }
}
